export type JsonPrimitive = string | number | boolean | null
export type JsonValue = JsonPrimitive | readonly JsonValue[] | { readonly [key: string]: JsonValue }
export type JsonObject = { readonly [key: string]: JsonValue }

export class CanonicalizationError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'CanonicalizationError'
  }
}

/**
 * RFC 8785 (JCS) serialisation.
 *
 * Three deliberate reuses of platform behaviour, each exactly matching the spec:
 *  - key ordering is UTF-16 code-unit order, which is JavaScript's default string sort;
 *  - string escaping is ECMAScript minimal escaping, which is `JSON.stringify` of a string;
 *  - numbers use ECMAScript Number::toString, which is `String(n)`.
 */
export function canonicalize(value: JsonValue): string {
  return write(value)
}

function write(value: JsonValue): string {
  if (value === null) return 'null'

  switch (typeof value) {
    case 'boolean':
      return value ? 'true' : 'false'
    case 'number':
      return writeNumber(value)
    case 'string':
      return JSON.stringify(value)
    case 'object': {
      if (Array.isArray(value)) {
        // A hole would be skipped by `map` and rendered as `[1,,3]` — invalid JSON.
        for (let i = 0; i < value.length; i++) {
          if (!(i in value)) {
            throw new CanonicalizationError(`sparse array: no element at index ${i}`)
          }
        }
        return `[${value.map(write).join(',')}]`
      }
      // Only plain objects are canonical JSON. Without this, a Date, Map, Set or
      // class instance has no own enumerable keys and serialises to `{}` — a
      // digest over nothing, undetectable until a replay fails years later.
      const prototype = Object.getPrototypeOf(value) as unknown
      if (prototype !== Object.prototype && prototype !== null) {
        throw new CanonicalizationError(
          `unsupported object: canonical JSON admits only plain objects and arrays`,
        )
      }
      const record = value as { readonly [key: string]: JsonValue }
      const keys = Object.keys(record).sort()
      const members = keys.map((key) => {
        const member = record[key]
        if (member === undefined) {
          throw new CanonicalizationError(`undefined value at key ${JSON.stringify(key)}`)
        }
        return `${JSON.stringify(key)}:${write(member)}`
      })
      return `{${members.join(',')}}`
    }
    default:
      throw new CanonicalizationError(`unsupported value of type ${typeof value}`)
  }
}

function writeNumber(value: number): string {
  if (!Number.isFinite(value)) {
    throw new CanonicalizationError(`non-finite number: ${String(value)}`)
  }
  if (Object.is(value, -0)) return '0'
  return String(value)
}
